import { useMemo, useState, isValidElement, cloneElement, Children } from "react";
import type { ReactNode } from "react";

export type DataTableColumn<T> = {
  key: keyof T | string;
  label: string;
  className?: string;
  sortable?: boolean;
  render?: (row: T) => ReactNode;
};

type SortDir = "asc" | "desc" | null;

type DataTableProps<T extends Record<string, unknown>> = {
  title?: string;
  rows: T[];
  columns: DataTableColumn<T>[];
  rowKey: (row: T) => string;
  headerAction?: ReactNode;
  searchPlaceholder?: string;
  pageSize?: number;
  exportFileName?: string;
  onDeleteRow?: (id: string) => void;
  deleteItemLabel?: string;
};

const STYLES = `
  .ns-dt-wrap { background:#fff; border-radius:16px; border:1px solid #f0ebe4; box-shadow:0 2px 12px rgba(26,18,8,.07); overflow:hidden; }
  .ns-dt-head { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:12px; padding:16px 20px; border-bottom:1px solid #f0ebe4; background:#fff; }
  .ns-dt-title { font-size:15px; font-weight:700; color:var(--title); letter-spacing:-.2px; }
  .ns-dt-controls { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .ns-dt-search-wrap { position:relative; }
  .ns-dt-search-ico { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#b09a88; pointer-events:none; }
  .ns-dt-search { padding:8px 12px 8px 34px; border:1.5px solid #ede6df; border-radius:10px; font-size:13px; outline:none; background:#fdf8f3; color:var(--title); width:210px; transition:border .2s,box-shadow .2s; font-family:inherit; }
  .ns-dt-search:focus { border-color:var(--theme); box-shadow:0 0 0 3px var(--theme-glow); background:#fff; }
  .ns-dt-export { display:flex; align-items:center; gap:5px; padding:8px 14px; border:1.5px solid #ede6df; border-radius:10px; font-size:13px; font-weight:600; cursor:pointer; background:#fdf8f3; color:var(--smoke); transition:all .15s; font-family:inherit; }
  .ns-dt-export:hover { border-color:var(--theme); color:var(--theme); background:var(--theme-light); }
  .ns-dt-scroll { overflow-x:auto; }
  .ns-dt-table { width:100%; border-collapse:collapse; }
  .ns-dt-table thead { background:#fdf8f3; }
  .ns-dt-table th { padding:11px 16px; font-size:11px; font-weight:700; color:var(--smoke); text-transform:uppercase; letter-spacing:.7px; border-bottom:1px solid #f0ebe4; white-space:nowrap; user-select:none; }
  .ns-dt-th-inner { display:inline-flex; align-items:center; gap:5px; }
  .ns-dt-sort-btn { background:none; border:none; cursor:pointer; padding:2px; display:flex; flex-direction:column; gap:2px; opacity:.35; transition:opacity .15s; }
  .ns-dt-sort-btn:hover { opacity:.7; }
  .ns-dt-sort-btn.asc,.ns-dt-sort-btn.desc { opacity:1; }
  .ns-dt-arrow { width:0; height:0; border-left:3.5px solid transparent; border-right:3.5px solid transparent; display:block; color:#b09a88; }
  .ns-dt-arrow-up { border-bottom:4px solid currentColor; }
  .ns-dt-arrow-down { border-top:4px solid currentColor; }
  .ns-dt-sort-btn.asc .ns-dt-arrow-up,.ns-dt-sort-btn.desc .ns-dt-arrow-down { color:var(--theme); }
  .ns-dt-table td { padding:13px 16px; border-bottom:1px solid #faf6f2; font-size:13.5px; color:var(--body-clr); vertical-align:middle; }
  .ns-dt-table tr:last-child td { border-bottom:none; }
  .ns-dt-table tbody tr { transition:background .12s; }
  .ns-dt-table tbody tr:hover td { background:#fdf8f3; }
  .ns-dt-empty-cell { text-align:center; padding:48px 20px; }
  .ns-dt-empty { display:flex; flex-direction:column; align-items:center; gap:8px; color:#b09a88; }
  .ns-dt-empty-ico { font-size:32px; opacity:.5; }
  .ns-dt-empty-txt { font-size:14px; }
  .ns-dt-foot { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:13px 20px; border-top:1px solid #f0ebe4; background:#fdf8f3; }
  .ns-dt-info { font-size:12px; color:#b09a88; font-weight:500; }
  .ns-dt-pages { display:flex; gap:4px; align-items:center; }
  .ns-dt-page-btn { width:32px; height:32px; border-radius:8px; border:1.5px solid #ede6df; background:#fff; cursor:pointer; font-size:13px; font-weight:600; color:var(--smoke); display:flex; align-items:center; justify-content:center; transition:all .15s; font-family:inherit; }
  .ns-dt-page-btn:hover:not(:disabled) { border-color:var(--theme); color:var(--theme); background:var(--theme-light); }
  .ns-dt-page-btn.ns-dt-page-active { background:var(--theme); border-color:var(--theme); color:#fff; box-shadow:0 2px 8px var(--theme-glow); }
  .ns-dt-page-btn:disabled { opacity:.35; cursor:not-allowed; }
  .ns-dt-page-dots { width:32px; text-align:center; font-size:13px; color:#b09a88; line-height:32px; }

  /* Delete Confirm Modal */
  .ns-del-overlay {
    position:fixed; inset:0; z-index:2000;
    background:rgba(26,18,8,.48);
    backdrop-filter:blur(4px);
    display:flex; align-items:center; justify-content:center; padding:20px;
    animation:ns-del-fade .15s ease;
  }
  @keyframes ns-del-fade { from{opacity:0} to{opacity:1} }
  .ns-del-modal {
    background:#fff; border-radius:20px;
    box-shadow:0 24px 64px rgba(26,18,8,.2);
    width:100%; max-width:400px; overflow:hidden;
    animation:ns-del-pop .2s cubic-bezier(.34,1.56,.64,1);
  }
  @keyframes ns-del-pop { from{opacity:0;transform:scale(.9)} to{opacity:1;transform:scale(1)} }
  .ns-del-top {
    display:flex; flex-direction:column; align-items:center; text-align:center;
    padding:36px 28px 22px; gap:14px;
  }
  .ns-del-icon {
    width:64px; height:64px; border-radius:50%;
    background:#fef2f2; border:2px solid #fecaca;
    display:flex; align-items:center; justify-content:center;
  }
  .ns-del-icon svg { color:#ef4444; }
  .ns-del-title { font-size:18px; font-weight:800; color:#1a1208; letter-spacing:-.3px; }
  .ns-del-desc { font-size:13.5px; color:#78716c; line-height:1.6; max-width:280px; }
  .ns-del-hr { height:1px; background:#f5f0eb; margin:0; }
  .ns-del-btns { display:flex; gap:10px; padding:18px 20px; }
  .ns-del-cancel {
    flex:1; padding:11px 0; border-radius:11px;
    border:1.5px solid #e8e0d8; background:#fdf8f3;
    font-size:14px; font-weight:600; color:#78716c;
    cursor:pointer; transition:all .15s; font-family:inherit;
  }
  .ns-del-cancel:hover { border-color:#c4b5a5; background:#f5ede4; color:#44403c; }
  .ns-del-confirm {
    flex:1; padding:11px 0; border-radius:11px;
    border:none; background:#ef4444;
    font-size:14px; font-weight:700; color:#fff;
    cursor:pointer; transition:all .15s; font-family:inherit;
    box-shadow:0 2px 10px rgba(239,68,68,.35);
  }
  .ns-del-confirm:hover { background:#dc2626; box-shadow:0 4px 16px rgba(239,68,68,.45); transform:translateY(-1px); }
  .ns-del-confirm:active { transform:translateY(0); }
`;

function exportCSV<T extends Record<string, unknown>>(rows: T[], columns: DataTableColumn<T>[], fileName: string) {
  const cols = columns.filter((c) => c.key !== "actions" && c.key !== "placeholder");
  const header = cols.map((c) => `"${c.label}"`).join(",");
  const dataRows = rows.map((row) =>
    cols.map((c) => `"${String((row as any)[c.key] ?? "").replace(/"/g, '""')}"`).join(",")
  );
  const csv = [header, ...dataRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${fileName}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function pageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}

/* Walk a React subtree and intercept buttons whose className includes
   "delete", "del", or "danger" — replace their onClick with the intercept fn */
function interceptDeleteBtns(node: ReactNode, rowId: string, fn: (id: string) => void): ReactNode {
  if (!isValidElement(node)) return node;
  const el = node as React.ReactElement<any>;
  const cls: string = el.props?.className ?? "";
  const isDelBtn = el.type === "button" && /delete|btn-del|danger/i.test(cls);
  const newChildren = el.props.children
    ? Children.map(el.props.children, (c) => interceptDeleteBtns(c, rowId, fn))
    : el.props.children;
  if (isDelBtn) {
    return cloneElement(el, {
      onClick: (e: React.MouseEvent) => { e.stopPropagation(); fn(rowId); },
      children: newChildren,
    });
  }
  return cloneElement(el, { children: newChildren });
}

export function DataTable<T extends Record<string, unknown>>({
  title, rows, columns, rowKey, headerAction,
  searchPlaceholder = "Search...", pageSize = 8,
  exportFileName, onDeleteRow, deleteItemLabel = "item",
}: DataTableProps<T>) {
  const [query, setQuery]           = useState("");
  const [page, setPage]             = useState(1);
  const [sortKey, setSortKey]       = useState<string | null>(null);
  const [sortDir, setSortDir]       = useState<SortDir>(null);
  const [pendingDelete, setPending] = useState<string | null>(null);

  function handleSort(key: string) {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir(null); }
    setPage(1);
  }

  const wrappedColumns = useMemo(() => {
    if (!onDeleteRow) return columns;
    return columns.map((col) => {
      if (!col.render) return col;
      return {
        ...col,
        render: (row: T) => interceptDeleteBtns(col.render!(row), rowKey(row as any), setPending),
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, onDeleteRow]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = q
      ? rows.filter((row) =>
          columns.some((col) => {
            if (col.render) return false;
            return String(row[col.key as keyof T] ?? "").toLowerCase().includes(q);
          })
        )
      : rows;
    if (sortKey && sortDir) {
      result = [...result].sort((a, b) => {
        const av = String(a[sortKey as keyof T] ?? "");
        const bv = String(b[sortKey as keyof T] ?? "");
        const an = Number(av), bn = Number(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : av.localeCompare(bv);
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return result;
  }, [rows, query, columns, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const start      = filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end        = Math.min(safePage * pageSize, filtered.length);

  return (
    <>
      <style>{STYLES}</style>
      <div className="ns-dt-wrap">

        {/* ── Header ── */}
        <div className="ns-dt-head">
          {title && <span className="ns-dt-title">{title}</span>}
          <div className="ns-dt-controls">
            {headerAction}
            {exportFileName && (
              <button type="button" className="ns-dt-export"
                onClick={() => exportCSV(filtered, columns, exportFileName)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
            )}
            <div className="ns-dt-search-wrap">
              <svg className="ns-dt-search-ico" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input className="ns-dt-search" type="search" placeholder={searchPlaceholder}
                value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="ns-dt-scroll">
          <table className="ns-dt-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.label} className={col.className}>
                    <div className="ns-dt-th-inner">
                      {col.label}
                      {col.sortable && (
                        <button type="button"
                          className={`ns-dt-sort-btn ${sortKey === col.key ? (sortDir ?? "") : ""}`}
                          onClick={() => handleSort(col.key as string)}
                          title={`Sort by ${col.label}`}>
                          <span className="ns-dt-arrow ns-dt-arrow-up" />
                          <span className="ns-dt-arrow ns-dt-arrow-down" />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length > 0 ? (
                paginated.map((row) => (
                  <tr key={rowKey(row)}>
                    {wrappedColumns.map((col) => (
                      <td key={col.label} className={col.className}>
                        {col.render ? col.render(row) : String(row[col.key as keyof T] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="ns-dt-empty-cell" colSpan={columns.length}>
                    <div className="ns-dt-empty">
                      <span className="ns-dt-empty-ico">🔍</span>
                      <span className="ns-dt-empty-txt">
                        No records found{query ? ` for "${query}"` : ""}.
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Footer ── */}
        <div className="ns-dt-foot">
          <span className="ns-dt-info">
            {filtered.length === 0 ? "No records" : `Showing ${start}–${end} of ${filtered.length} records`}
          </span>
          {totalPages > 1 && (
            <div className="ns-dt-pages">
              <button type="button" className="ns-dt-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
              {pageNumbers(safePage, totalPages).map((p, i) =>
                p === "..." ? (
                  <span key={`e${i}`} className="ns-dt-page-dots">…</span>
                ) : (
                  <button key={p} type="button"
                    className={`ns-dt-page-btn${p === safePage ? " ns-dt-page-active" : ""}`}
                    onClick={() => setPage(p as number)}>{p}</button>
                )
              )}
              <button type="button" className="ns-dt-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Delete Confirm Modal ── */}
      {pendingDelete && (
        <div
          className="ns-del-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setPending(null); }}
        >
          <div className="ns-del-modal" role="dialog" aria-modal="true">
            <div className="ns-del-top">
              <div className="ns-del-icon">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </div>
              <div className="ns-del-title">Delete {deleteItemLabel}?</div>
              <p className="ns-del-desc">
                This action cannot be undone. This {deleteItemLabel.toLowerCase()} will be permanently removed.
              </p>
            </div>
            <div className="ns-del-hr" />
            <div className="ns-del-btns">
              <button type="button" className="ns-del-cancel" onClick={() => setPending(null)}>
                Cancel
              </button>
              <button type="button" className="ns-del-confirm" onClick={() => {
                if (pendingDelete && onDeleteRow) onDeleteRow(pendingDelete);
                setPending(null);
              }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
